import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ref, get } from "firebase/database";
import { db } from "../services/firebase";
import { 
  FaCode, 
  FaClock, 
  FaCheck, 
  FaTimes, 
  FaChevronLeft, 
  FaChevronRight, 
  FaUniversity, 
  FaFileAlt, 
  FaInfoCircle, 
  FaTimes as FaClose, 
  FaCircle,
  FaExclamationTriangle,
  FaArrowRight,
  FaBook,
  FaSearch,
  FaBullseye,
  FaSync,
  FaChartBar,
  FaPlay,
  FaStopwatch,
  FaExclamationCircle,
  FaMap,
  FaCheckCircle,
  FaChevronUp,
  FaChevronDown,
  FaGraduationCap,
  FaCode as FaFileCode,
  FaBug,
  FaEye,
  FaCogs,
  FaLightbulb
} from "react-icons/fa";
import CodeSnippetResult from "./CodeSnippetResult";
import "./TestCodeSnippet.css";

function TestCodeSnippet({ user }) {
  const { quizId } = useParams();
  const [quizData, setQuizData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [visitedQuestions, setVisitedQuestions] = useState(new Set([0])); // Start with first question visited
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);


  // Shuffle function to randomize question order
  const shuffleQuestions = (questionsArray) => {
    const shuffled = [...questionsArray];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        const quizRef = ref(db, `codeSnippetQuestions/${quizId}`);
        const snapshot = await get(quizRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          // Shuffle the questions before setting them
          const questionsArray = Object.values(data.questions || {});
          const shuffledQuestions = shuffleQuestions(questionsArray);
          
          // Update the data with shuffled questions as an array
          const updatedData = {
            ...data,
            questions: shuffledQuestions
          };
          
          setQuizData(updatedData);
          setAnswers(new Array(shuffledQuestions.length).fill(null));
          
          if (data.countdown) {
            setTimeLeft(parseInt(data.countdown) * 60);
          }
        } else {
          setError('Quiz not found');
        }
      } catch (error) {
        console.error('Error fetching quiz:', error);
        setError('Failed to load quiz');
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [quizId]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleAnswerSelect = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answer
    }));
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setVisitedQuestions(prev => new Set([...prev, nextIndex]));
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      setVisitedQuestions(prev => new Set([...prev, prevIndex]));
    }
  };

  // const handleSubmit = () => {
  //   setShowResults(true);
  // };

  const handleSubmit = () => {
  if (hasSubmitted) return; // prevent double submission

  setHasSubmitted(true);
  setShowResults(true);

  // TODO: Save the result here if needed
};

  const calculateScore = () => {
    if (!quizData) return { correct: 0, total: 0, percentage: 0 };

    const questions = quizData.questions;
    let correct = 0;
    let totalMarks = 0;

    questions.forEach((question, index) => {
      if (answers[index] === question.correctAnswer) {
        correct++;
        totalMarks += question.marks || 1;
      }
    });

    return { 
      correct, 
      total: questions.reduce((sum, q) => sum + (q.marks || 1), 0),
      totalMarks,
      percentage: Math.round((correct / questions.length) * 100) 
    };
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (index) => {
    const userAnswer = answers[index];
    const correctAnswer = questions[index].correctAnswer;
    
    if (showResults) {
      // After submit - show correct/wrong/unanswered
      if (!userAnswer) return 'unanswered';
      return userAnswer === correctAnswer ? 'correct' : 'wrong';
    } else {
      // Before submit - show current/visited/answered/unanswered
      if (index === currentQuestionIndex) return 'current';
      if (userAnswer) return 'answered';
      if (visitedQuestions.has(index)) return 'visited';
      return 'unanswered';
    }
  };

  const handleQuestionClick = (index) => {
    setCurrentQuestionIndex(index);
    setVisitedQuestions(prev => new Set([...prev, index]));
  };

  if (loading) {
    return (
      <div className="testCodeSnippet-container">
        <div className="testCodeSnippet-loading">Loading quiz...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="testCodeSnippet-container">
        <div className="testCodeSnippet-error">{error}</div>
      </div>
    );
  }

  if (!quizData) {
    return (
      <div className="testCodeSnippet-container">
        <div className="testCodeSnippet-error">Quiz not found</div>
      </div>
    );
  }

  const questions = quizData.questions;
  const currentQuestion = questions[currentQuestionIndex];

  if (showResults) {
    return (
      <CodeSnippetResult
        user={user}
        quizData={quizData}
        answers={answers}
        timeLeft={timeLeft}
        isFromTest={true}
      />
    );
  }

  return (
    <div className="testCodeSnippet-container">
      {/* Header */}
      <div className="testCodeSnippet-header">
        <div className="testCodeSnippet-header-left">
          <FaUniversity className="testCodeSnippet-university-logo" />
          <div className="testCodeSnippet-university-info">
            <h2>BodhAI </h2>
            <p>Code Snippet Assessment</p>
          </div>
        </div>
        
        <div className="testCodeSnippet-header-center">
          <button className="testCodeSnippet-instructions-btn" onClick={() => setShowInstructions(true)}>
            <FaInfoCircle /> Instructions
          </button>
        </div>

        <div className="testCodeSnippet-header-right">
          <div className="testCodeSnippet-marks-info">
            <span>Total Max Marks: {questions.reduce((sum, q) => sum + (q.marks || 1), 0)}</span>
            {showResults && (
              <span>Total Marks Obtained: {questions.reduce((sum, q, i) => {
                const userAnswer = answers[i];
                const correctAnswer = q.correctAnswer;
                return sum + (userAnswer && userAnswer === correctAnswer ? (q.marks || 1) : 0);
              }, 0)}</span>
            )}
          </div>
          
          {timeLeft !== null && (
            <div className="testCodeSnippet-timer">
              <FaClock /> {formatTime(timeLeft)}
            </div>
          )}
        </div>
      </div>

      <div className="testCodeSnippet-content">
        {/* Left Sidebar */}
        <div className={`testCodeSnippet-sidebar ${sidebarOpen ? 'testCodeSnippet-sidebar-open' : 'testCodeSnippet-sidebar-closed'}`}>
          <div className="testCodeSnippet-sidebar-header">
            {sidebarOpen ? (
              // Open sidebar - show user info and controls
              <div className="testCodeSnippet-sidebar-user-info">
                <div className="testCodeSnippet-user-details">
                  <span className="testCodeSnippet-user-name">{user?.displayName || user?.email || 'User'}</span>
                  <span className="testCodeSnippet-progress-info">{questions.filter((_, i) => answers[i]).length} of {questions.length} questions answered</span>
                </div>
                <button 
                  className="testCodeSnippet-sidebar-btn"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <FaChevronLeft />
                </button>
              </div>
            ) : (
              // Closed sidebar - show only control icons
              <div className="testCodeSnippet-sidebar-controls">
                <button className="testCodeSnippet-sidebar-btn" onClick={() => setShowInstructions(true)}>
                  <FaFileAlt />
                </button>
                <button 
                  className="testCodeSnippet-sidebar-btn"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>

          {sidebarOpen ? (
            // Open sidebar content
            <>
              <div className="testCodeSnippet-instructions-section" onClick={() => setShowInstructions(true)}>
                <div className="testCodeSnippet-instructions-header">
                  <FaFileAlt />
                  <span>Instructions</span>
                </div>
              </div>
              
              <div className="testCodeSnippet-legend">
                <h4>Question Status</h4>
                {showResults ? (
                  // After submit - show correct/wrong/unanswered
                  <>
                    <div className="testCodeSnippet-legend-item">
                      <div className="testCodeSnippet-legend-circle testCodeSnippet-legend-correct"></div>
                      <span>Correct Answered</span>
                    </div>
                    <div className="testCodeSnippet-legend-item">
                      <div className="testCodeSnippet-legend-circle testCodeSnippet-legend-wrong"></div>
                      <span>Wrong Answered</span>
                    </div>
                    <div className="testCodeSnippet-legend-item">
                      <div className="testCodeSnippet-legend-circle testCodeSnippet-legend-unanswered"></div>
                      <span>Unanswered</span>
                    </div>
                  </>
                ) : (
                  // Before submit - show current/visited/answered/unanswered
                  <>
                    <div className="testCodeSnippet-legend-item">
                      <div className="testCodeSnippet-legend-circle testCodeSnippet-legend-current"></div>
                      <span>Current Question</span>
                    </div>
                    <div className="testCodeSnippet-legend-item">
                      <div className="testCodeSnippet-legend-circle testCodeSnippet-legend-visited"></div>
                      <span>Visited</span>
                    </div>
                    <div className="testCodeSnippet-legend-item">
                      <div className="testCodeSnippet-legend-circle testCodeSnippet-legend-answered"></div>
                      <span>Answered</span>
                    </div>
                    <div className="testCodeSnippet-legend-item">
                      <div className="testCodeSnippet-legend-circle testCodeSnippet-legend-unanswered"></div>
                      <span>Unanswered</span>
                    </div>
                  </>
                )}
              </div>

              <div className="testCodeSnippet-question-navigation">
                <div className="testCodeSnippet-nav-section">
                  <h4><FaCode /> Code Snippet Questions</h4>
                  <div className="testCodeSnippet-question-buttons">
                    {questions.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuestionClick(index)}
                        className={`testCodeSnippet-question-btn testCodeSnippet-question-${getQuestionStatus(index)}`}
                        title={`Question ${index + 1}`}
                      >
                        <span className="testCodeSnippet-question-number">{index + 1}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Closed sidebar content - vertical list only
            <div className="testCodeSnippet-collapsed-navigation">
              <div className="testCodeSnippet-scroll-up" onClick={() => {
                const container = document.querySelector('.testCodeSnippet-collapsed-question-buttons');
                if (container) {
                  container.scrollTop -= 100;
                }
              }}>
                <FaChevronUp />
              </div>
              <div className="testCodeSnippet-collapsed-question-buttons">
                {questions.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionClick(index)}
                    className={`testCodeSnippet-collapsed-question-btn testCodeSnippet-question-${getQuestionStatus(index)}`}
                    title={`Question ${index + 1}`}
                  >
                    <span className="testCodeSnippet-question-number">{index + 1}</span>
                  </button>
                ))}
              </div>
              <div className="testCodeSnippet-scroll-down" onClick={() => {
                const container = document.querySelector('.testCodeSnippet-collapsed-question-buttons');
                if (container) {
                  container.scrollTop += 100;
                }
              }}>
                <FaChevronDown />
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className={`testCodeSnippet-main-content ${sidebarOpen ? 'testCodeSnippet-main-with-sidebar' : 'testCodeSnippet-main-full-width'}`}>
          {sidebarOpen ? (
            // Old format when sidebar is open
            <div className="testCodeSnippet-question-panel">
              <div className="testCodeSnippet-question-header">
                <h3>Question: {currentQuestionIndex + 1}</h3>
                <div className="testCodeSnippet-question-meta">
                  <span className="testCodeSnippet-language-tag">{currentQuestion.language}</span>
                  <span className="testCodeSnippet-type-tag">{currentQuestion.questionType}</span>
                  <span className="testCodeSnippet-marks-tag">{currentQuestion.marks} marks</span>
                </div>
              </div>

              <div className="testCodeSnippet-question-content">
                <h4>{currentQuestion.title}</h4>
                <p className="testCodeSnippet-question-description">{currentQuestion.description}</p>
                
                <div className="testCodeSnippet-code-section">
                  <h5>Code Snippet:</h5>
                  <div className="testCodeSnippet-code-display">
                    <pre>{currentQuestion.codeSnippet}</pre>
                  </div>
                </div>
              </div>

              <div className="testCodeSnippet-answer-panel">
                <h4>Select the correct answer:</h4>
                <div className="testCodeSnippet-options-section">
                  {currentQuestion.options.map((option, index) => (
                    <label key={index} className={`testCodeSnippet-option-label ${answers[currentQuestionIndex] === option ? 'testCodeSnippet-option-selected' : ''}`}>
                      <input
                        type="radio"
                        name={`question-${currentQuestionIndex}`}
                        value={option}
                        checked={answers[currentQuestionIndex] === option}
                        onChange={() => handleAnswerSelect(option)}
                      />
                      <span className="testCodeSnippet-option-text">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="testCodeSnippet-navigation-buttons">
                <button
                  onClick={prevQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="testCodeSnippet-nav-btn testCodeSnippet-nav-prev"
                >
                  <FaChevronLeft /> Previous
                </button>

                {currentQuestionIndex === questions.length - 1 ? (
                  <button onClick={handleSubmit} className="testCodeSnippet-submit-btn">
                    Submit Quiz
                  </button>
                ) : (
                  <button onClick={nextQuestion} className="testCodeSnippet-nav-btn testCodeSnippet-nav-next">
                    Next <FaChevronRight />
                  </button>
                )}
              </div>
            </div>
          ) : (
            // New format when sidebar is closed - split layout
            <div className="testCodeSnippet-split-layout">
              <div className="testCodeSnippet-question-side">
                <div className="testCodeSnippet-question-header">
                  <h3>Question: {currentQuestionIndex + 1}</h3>
                  <div className="testCodeSnippet-question-meta">
                    <span className="testCodeSnippet-language-tag">{currentQuestion.language}</span>
                    <span className="testCodeSnippet-type-tag">{currentQuestion.questionType}</span>
                    <span className="testCodeSnippet-marks-tag">{currentQuestion.marks} marks</span>
                  </div>
                </div>

                <div className="testCodeSnippet-question-content">
                  <h4>{currentQuestion.title}</h4>
                  <p className="testCodeSnippet-question-description">{currentQuestion.description}</p>
                  
                  <div className="testCodeSnippet-code-section">
                    <h5>Code Snippet:</h5>
                    <div className="testCodeSnippet-code-display">
                      <pre>{currentQuestion.codeSnippet}</pre>
                    </div>
                  </div>
                </div>
              </div>

              <div className="testCodeSnippet-options-side">
                <h4>Select the correct answer:</h4>
                <div className="testCodeSnippet-options-section">
                  {currentQuestion.options.map((option, index) => (
                    <label key={index} className={`testCodeSnippet-option-label ${answers[currentQuestionIndex] === option ? 'testCodeSnippet-option-selected' : ''}`}>
                      <input
                        type="radio"
                        name={`question-${currentQuestionIndex}`}
                        value={option}
                        checked={answers[currentQuestionIndex] === option}
                        onChange={() => handleAnswerSelect(option)}
                      />
                      <span className="testCodeSnippet-option-text">{option}</span>
                    </label>
                  ))}
                </div>

                <div className="testCodeSnippet-navigation-buttons">
                  <button
                    onClick={prevQuestion}
                    disabled={currentQuestionIndex === 0}
                    className="testCodeSnippet-nav-btn testCodeSnippet-nav-prev"
                  >
                    <FaChevronLeft /> Previous
                  </button>

                  {currentQuestionIndex === questions.length - 1 ? (
                    <button onClick={handleSubmit} className="testCodeSnippet-submit-btn">
                      Submit Quiz
                    </button>
                  ) : (
                    <button onClick={nextQuestion} className="testCodeSnippet-nav-btn testCodeSnippet-nav-next">
                      Next <FaChevronRight />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      {showResults && (
        <div className="testCodeSnippet-results-section">
          <div className="testCodeSnippet-score-card">
            <h3>Assessment Results</h3>
            <div className="testCodeSnippet-score">
              {calculateScore().percentage}%
            </div>
            <p>You answered {calculateScore().correct} out of {calculateScore().total} questions correctly</p>
            <p>Total Marks: {calculateScore().totalMarks} out of {questions.reduce((sum, q) => sum + (q.marks || 1), 0)}</p>
          </div>

          <div className="testCodeSnippet-questions-review">
            <h3>Question Review</h3>
            {questions.map((question, index) => (
              <div key={index} className="testCodeSnippet-question-review">
                <h4>Question {index + 1}</h4>
                <div className="testCodeSnippet-question-content">
                  <h5>{question.title}</h5>
                  <p>{question.description}</p>
                  <div className="testCodeSnippet-code-section">
                    <h5>Code Snippet:</h5>
                    <div className="testCodeSnippet-code-display">
                      <pre>{question.codeSnippet}</pre>
                    </div>
                  </div>
                </div>

                <div className="testCodeSnippet-answer-section">
                  <h5>Your Answer:</h5>
                  <div className={`testCodeSnippet-selected-answer ${answers[index] ? '' : 'testCodeSnippet-no-answer'}`}>
                    {answers[index] || 'No answer provided'}
                  </div>
                </div>

                <div className="testCodeSnippet-correct-answer-section">
                  <h5>Correct Answer:</h5>
                  <div className="testCodeSnippet-correct-answer">
                    {question.correctAnswer}
                  </div>
                </div>

                {question.explanation && (
                  <div className="testCodeSnippet-explanation-section">
                    <h5>Explanation:</h5>
                    <div className="testCodeSnippet-explanation">
                      {question.explanation}
                    </div>
                  </div>
                )}

                <div className={`testCodeSnippet-result-indicator testCodeSnippet-${getQuestionStatus(index)}`}>
                  {getQuestionStatus(index) === 'correct' && <FaCheck />}
                  {getQuestionStatus(index) === 'wrong' && <FaTimes />}
                  {getQuestionStatus(index) === 'unanswered' && <FaExclamationCircle />}
                  <span>
                    {getQuestionStatus(index) === 'correct' && 'Correct'}
                    {getQuestionStatus(index) === 'wrong' && 'Incorrect'}
                    {getQuestionStatus(index) === 'unanswered' && 'Unanswered'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions Popup */}
      {showInstructions && (
        <div className="instructions-popup-overlay" onClick={() => setShowInstructions(false)}>
          <div className="instructions-popup" onClick={(e) => e.stopPropagation()}>
            <div className="instructions-popup-header">
              <div className="header-content">
                <FaFileAlt className="header-icon" />
                <h3>Test Instructions</h3>
              </div>
              <button 
                className="instructions-close-btn" 
                onClick={() => setShowInstructions(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="instructions-popup-content">
              <div className="instruction-section">
                <h4><FaGraduationCap /> How to Take the Test</h4>
                <ul>
                  <li><FaCheckCircle /> Read each question carefully</li>
                  <li><FaCheckCircle /> Analyze the code snippet provided</li>
                  <li><FaCheckCircle /> Select the correct answer from options</li>
                  <li><FaCheckCircle /> Use navigation to move between questions</li>
                  <li><FaCheckCircle /> Review answers before submitting</li>
                </ul>
              </div>

              <div className="instruction-section">
                <h4><FaClock /> Time Management</h4>
                <ul>
                  <li><FaInfoCircle /> Timer displayed in header</li>
                  <li><FaExclamationTriangle /> Auto-submit when time expires</li>
                  <li><FaMap /> Track progress in sidebar</li>
                  <li><FaSync /> Change answers before submitting</li>
                </ul>
              </div>

              <div className="instruction-section">
                <h4><FaBullseye /> Scoring & Navigation</h4>
                <ul>
                  <li><FaChartBar /> Each question has specific marks</li>
                  <li><FaCircle className="correct-icon" /> Green = Correctly answered</li>
                  <li><FaCircle className="wrong-icon" /> Red = Incorrectly answered</li>
                  <li><FaCircle className="unanswered-icon" /> White = Unanswered questions</li>
                  <li><FaCircle className="current-icon" /> Blue = Current question</li>
                </ul>
              </div>

              <div className="instruction-section">
                <h4><FaCode /> Question Types</h4>
                <ul>
                  <li><FaSearch /> <strong>Code Analysis:</strong> Analyze code structure</li>
                  <li><FaFileCode /> <strong>Pseudocode:</strong> Convert to pseudocode</li>
                  <li><FaBug /> <strong>Debugging:</strong> Find and fix errors</li>
                  <li><FaEye /> <strong>Output Prediction:</strong> Predict program output</li>
                  <li><FaCogs /> <strong>Algorithm Understanding:</strong> Understand logic</li>
                </ul>
              </div>

              <div className="test-overview">
                <h4><FaInfoCircle /> Test Overview</h4>
                <div className="overview-grid">
                  <div className="overview-item">
                    <FaCode />
                    <span>Total Questions: {questions.length}</span>
                  </div>
                  <div className="overview-item">
                    <FaClock />
                    <span>Time Limit: {quizData.countdown ? `${quizData.countdown} min` : 'No limit'}</span>
                  </div>
                  <div className="overview-item">
                    <FaChartBar />
                    <span>Total Marks: {questions.reduce((sum, q) => sum + (q.marks || 1), 0)}</span>
                  </div>
                </div>
              </div>

              <div className="pro-tip">
                <h4><FaLightbulb /> Pro Tip</h4>
                <ul>
                  <li><FaCheckCircle /> Read the entire code snippet before answering</li>
                  <li><FaCheckCircle /> Pay attention to variable names and data types</li>
                  <li><FaCheckCircle /> Look for edge cases and boundary conditions</li>
                  <li><FaCheckCircle /> Use the sidebar to track your progress</li>
                  <li><FaCheckCircle /> Don't rush - accuracy is more important than speed</li>
                </ul>
              </div>
            </div>

            <div className="instructions-popup-footer">
              <button 
                className="start-test-btn" 
                onClick={() => setShowInstructions(false)}
              >
                <FaPlay />
                <span>Start Test</span>
                <FaArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestCodeSnippet; 